/**
 * "Selling fast" cart callout controller.
 *
 * Requirements:
 *  1. First-time add-to-cart in a session -> show after `first_time_delay`
 *     seconds of continued browsing (not checkout).
 *  2. A cart that already has item(s) -> show after `returning_shopper_delay`
 *     seconds of browsing. This covers both: still browsing later in the same
 *     session, and a fresh visit on another day where the cart persisted but
 *     sessionStorage was cleared (new browser session).
 *  3. Never shown on the cart page.
 *  4. Shown at most once per session.
 *
 * Reliability note: a plain in-memory setTimeout does NOT survive page
 * navigation (shoppers browse across multiple pages while a timer is
 * pending). To make the schedule reliable, the "show at" moment is stored as
 * an absolute timestamp in sessionStorage. Every page load re-derives the
 * remaining delay from that timestamp and re-arms a local setTimeout for
 * exactly what's left, instead of trusting an in-memory handle or a stale
 * boolean flag that could never be cleared once its owning page unloaded.
 *
 * Safe to evaluate more than once (guarded by window.__cartRemainderCalloutInit).
 */
(function () {
  if (window.__cartRemainderCalloutInit) {
    return;
  }
  window.__cartRemainderCalloutInit = true;

  var SESSION_SHOWN_KEY = 'cart_remainder_shown';
  var SESSION_SHOW_AT_KEY = 'cart_remainder_show_at';

  /* ------------------------------------------------------------------ */
  /* Defensive sessionStorage wrapper                                   */
  /* ------------------------------------------------------------------ */
  var memoryFallback = {};

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return Object.prototype.hasOwnProperty.call(memoryFallback, key) ? memoryFallback[key] : null;
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      memoryFallback[key] = value;
    }
  }

  function storageRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      delete memoryFallback[key];
    }
  }

  var SessionState = {
    hasShown: function () {
      return storageGet(SESSION_SHOWN_KEY) === 'true';
    },
    markShown: function () {
      storageSet(SESSION_SHOWN_KEY, 'true');
    },
    getShowAt: function () {
      var value = storageGet(SESSION_SHOW_AT_KEY);
      var timestamp = value ? parseInt(value, 10) : NaN;
      return isNaN(timestamp) ? null : timestamp;
    },
    setShowAt: function (timestamp) {
      storageSet(SESSION_SHOW_AT_KEY, String(timestamp));
    },
    clearShowAt: function () {
      storageRemove(SESSION_SHOW_AT_KEY);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Cart state abstraction                                             */
  /* ------------------------------------------------------------------ */
  function normalizeCart(cart) {
    if (!cart || typeof cart !== 'object') {
      return { itemCount: 0, hasSaleItem: false };
    }

    var items = cart.items || cart.line_items || [];
    var hasSaleItem = false;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var comparePrice = item.compare_at_price || item.original_price;
      var price = item.final_price || item.price;

      if (comparePrice && price && Number(comparePrice) > Number(price)) {
        hasSaleItem = true;
        break;
      }
    }

    var itemCount = typeof cart.item_count === 'number' ? cart.item_count : items.length;

    return { itemCount: itemCount, hasSaleItem: hasSaleItem };
  }

  function fetchCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(normalizeCart)
      .catch(function () {
        return { itemCount: 0, hasSaleItem: false };
      });
  }

  /* ------------------------------------------------------------------ */
  /* Page / checkout detection                                          */
  /* ------------------------------------------------------------------ */
  function isCartPage() {
    var pageType = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType;
    return pageType === 'cart' || window.location.pathname.replace(/\/$/, '') === '/cart';
  }

  function isCheckoutInProgress() {
    return /\/checkouts?\//.test(window.location.pathname) || window.location.pathname.indexOf('/checkout') === 0;
  }

  /* ------------------------------------------------------------------ */
  /* Timer manager                                                       */
  /*                                                                      */
  /* Only one "show at" schedule is tracked at a time (in sessionStorage) */
  /* so the pending display survives navigation across pages. The local  */
  /* setTimeout handle only controls the CURRENT page's wake-up call;    */
  /* the source of truth for "when" is always the stored timestamp.      */
  /* ------------------------------------------------------------------ */
  var activeTimeoutId = null;

  function clearLocalTimeout() {
    if (activeTimeoutId !== null) {
      window.clearTimeout(activeTimeoutId);
      activeTimeoutId = null;
    }
  }

  function cancelSchedule() {
    clearLocalTimeout();
    SessionState.clearShowAt();
  }

  // Arms a local setTimeout that fires at `showAt` (absolute ms timestamp),
  // regardless of how much time is actually left on this particular page load.
  function armTimeoutFor(controller, showAt) {
    clearLocalTimeout();
    var remaining = Math.max(0, showAt - Date.now());
    activeTimeoutId = window.setTimeout(function () {
      activeTimeoutId = null;
      controller.evaluateAndDisplay();
    }, remaining);
  }

  // Schedules a new "show at" timestamp only if nothing is already pending.
  function scheduleShow(controller, delaySeconds) {
    if (SessionState.hasShown()) {
      return;
    }
    if (SessionState.getShowAt() !== null) {
      return; // a schedule is already pending; don't reset/extend it
    }

    var showAt = Date.now() + delaySeconds * 1000;
    SessionState.setShowAt(showAt);
    armTimeoutFor(controller, showAt);
  }

  // Re-arms whatever schedule was already pending from a previous page load.
  function resumePendingSchedule(controller) {
    var showAt = SessionState.getShowAt();
    if (showAt === null) {
      return;
    }
    if (Date.now() >= showAt) {
      // Time already elapsed while we were on a previous page (or the tab
      // was inactive); evaluate right away.
      activeTimeoutId = null;
      controller.evaluateAndDisplay();
      return;
    }
    armTimeoutFor(controller, showAt);
  }

  /* ------------------------------------------------------------------ */
  /* Controller                                                          */
  /* ------------------------------------------------------------------ */
  function CartRemainderController(el) {
    this.el = el;
    this.firstTimeDelay = parseFloat(el.getAttribute('data-first-time-delay')) || 60;
    this.returningShopperDelay = parseFloat(el.getAttribute('data-returning-shopper-delay')) || 15;
    this.showOncePerSession = el.getAttribute('data-show-once-per-session') === 'true';
    this.hideOnCartPage = el.getAttribute('data-hide-on-cart-page') === 'true';
    this.saleItemsOnly = el.getAttribute('data-sale-items-only') === 'true';
    this.debugMode = el.getAttribute('data-debug-mode') === 'true';
    this.closeOnOutsideClick = el.getAttribute('data-close-on-outside-click') !== 'false';

    this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onCloseClick = this.hide.bind(this);
  }

  CartRemainderController.prototype.canConsiderDisplay = function () {
    if (this.showOncePerSession && SessionState.hasShown()) {
      return false;
    }
    if (this.hideOnCartPage && isCartPage()) {
      return false;
    }
    if (isCheckoutInProgress()) {
      return false;
    }
    return true;
  };

  CartRemainderController.prototype.evaluateAndDisplay = function () {
    var self = this;

    // The schedule is consumed here (win or lose) so a stale pending
    // timestamp can never get "stuck" and block future scheduling.
    SessionState.clearShowAt();

    if (!self.canConsiderDisplay()) {
      return;
    }

    fetchCart().then(function (cart) {
      if (!self.canConsiderDisplay()) {
        return;
      }
      if (cart.itemCount <= 0) {
        return;
      }
      if (self.saleItemsOnly && !cart.hasSaleItem) {
        return;
      }
      self.show();
    });
  };

  CartRemainderController.prototype.show = function () {
    if (!this.debugMode && this.showOncePerSession && SessionState.hasShown()) {
      return;
    }

    this.el.classList.add('is-visible');
    this.el.setAttribute('aria-hidden', 'false');

    if (!this.debugMode && this.showOncePerSession) {
      SessionState.markShown();
    }

    var closeButton = this.el.querySelector('.cart-remainder-callout__close');
    if (closeButton) {
      closeButton.addEventListener('click', this._onCloseClick);
    }

    document.addEventListener('keydown', this._onDocumentKeydown);
    document.addEventListener('click', this._onDocumentClick, { capture: true, passive: true });
  };

  CartRemainderController.prototype.hide = function () {
    this.el.classList.remove('is-visible');
    this.el.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this._onDocumentKeydown);
    document.removeEventListener('click', this._onDocumentClick, { capture: true });
  };

  CartRemainderController.prototype._onDocumentKeydown = function (event) {
    if (event.key === 'Escape') {
      this.hide();
    }
  };

  CartRemainderController.prototype._onDocumentClick = function (event) {
    if (this.closeOnOutsideClick && !this.el.contains(event.target)) {
      this.hide();
    }
  };

  CartRemainderController.prototype.handleCartUpdate = function (cart) {
    if (!this.canConsiderDisplay()) {
      cancelSchedule();
      return;
    }
    if (cart.itemCount <= 0) {
      cancelSchedule();
      return;
    }
    scheduleShow(this, this.firstTimeDelay);
  };

  CartRemainderController.prototype.handlePersistedCart = function (cart) {
    if (!this.canConsiderDisplay()) {
      return;
    }
    if (cart.itemCount <= 0) {
      return;
    }
    scheduleShow(this, this.returningShopperDelay);
  };

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */
  function init() {
    var el = document.querySelector('.header__cart-remainder-tooltip');
    if (!el) {
      return;
    }

    var controller = new CartRemainderController(el);

    if (controller.debugMode) {
      // Design/preview only: skip delays, session state, and cart checks so
      // the popup is visible immediately without waiting on trigger conditions.
      controller.show();
      return;
    }

    if (isCheckoutInProgress()) {
      cancelSchedule();
    } else {
      // Re-arm any schedule that was already pending from a previous page
      // load in this same session (e.g. shopper added to cart, then browsed
      // to another page before the delay elapsed).
      resumePendingSchedule(controller);
    }

    // Native theme cart events (see assets/theme.js.liquid: cart:change / variant:add).
    document.addEventListener('cart:change', function (event) {
      controller.handleCartUpdate(normalizeCart(event.detail && event.detail.cart));
    });

    document.addEventListener('variant:add', function (event) {
      controller.handleCartUpdate(normalizeCart(event.detail && event.detail.cart));
    });

    document.addEventListener('cart:refresh', function () {
      fetchCart().then(function (cart) {
        if (cart.itemCount <= 0) {
          cancelSchedule();
        }
      });
    });

    // Rebuy cart events: https://developers.rebuyengine.com/reference/cart-event-listeners/
    // NOTE: `event.detail.cart` is the Rebuy Cart *module*, not the Shopify
    // cart. The actual Shopify cart payload lives at `event.detail.cart.cart`.
    // Using the module directly makes normalizeCart() see itemCount 0 on
    // every add/change, which was wiping out the pending schedule.
    function extractShopifyCart(event) {
      return event.detail && event.detail.cart && event.detail.cart.cart;
    }

    document.addEventListener('rebuy:cart.ready', function (event) {
      controller.handlePersistedCart(normalizeCart(extractShopifyCart(event)));
    });

    document.addEventListener('rebuy:cart.add', function (event) {
      controller.handleCartUpdate(normalizeCart(extractShopifyCart(event)));
    });

    document.addEventListener('rebuy:cart.change', function (event) {
      controller.handleCartUpdate(normalizeCart(extractShopifyCart(event)));
    });

    // Cancel any pending schedule if the customer heads to checkout.
    window.addEventListener('pagehide', function () {
      if (isCheckoutInProgress()) {
        cancelSchedule();
      }
    });

    // Persisted-cart path: only needed when nothing was already scheduled
    // (either from a prior page in this session, or just resumed above).
    if (SessionState.getShowAt() === null && !SessionState.hasShown() && !isCartPage() && !isCheckoutInProgress()) {
      fetchCart().then(function (cart) {
        controller.handlePersistedCart(cart);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
